import React, { createContext, useEffect, useState, useCallback } from 'react';
import cn from 'classnames/dedupe';
import parse, { attributesToProps } from 'html-react-parser';
import Head from 'next/head';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';
import ReactDOMServer from 'react-dom/server';
import { Box } from '@mui/material';

import dynamic from 'next/dynamic';
const TrustpilotReviews = dynamic(() => import('../components/TrustpilotReviews.js'), { ssr: false });

import config from '../config/config';
import i18next from '../language/i18next';
import { getPage } from '../api/page/page';
import { getMenuItems } from '../api/menu/menu';
import { BrokerList, CarouselQuotation, HeaderWithMenu, CtaBanner, ProvincesList, StepQuestions } from '../components';
import { renderCarouselQuotationServerSide } from '../components/carouselQuotation/CarouselQuotation';
import { renderProvincesListServerSide } from '../components/provincesList/ProvincesList';
import { renderBrokerListServerSide } from '../components/brokersList/BrokerList';
import Breadcrumbs from '../components/breadcrumbs/Breadcrumbs';
import TextualQuotation, { renderTextualQuotationServerSide } from '../components/textualQuotation/TextualQuotation';
import ResultsQuotation from '../components/resultsQuotation/ResultsQuotation';
import { renderResultsQuotationServerSide } from '../components/resultsQuotation/useResultsQuotation';

import getProductSchema from '../api/schemaorg/getProductSchema.js';
import getBreadcrumbsSchema from '../api/schemaorg/getBreadcrumbsSchema.js';
import { useOnRenderedContext } from '../context/OnRenderedContext.js';

import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../store/useStore';
import Price from './cerca-mutuo/components/feedback/questionsType/price';
import Stepper from './cerca-mutuo/components/feedback/stepper';
import styles from './cerca-mutuo/components/feedback/styles/StepQuestion.module.css';
import questionStyles from '../components/feedback/styles/question.module.css';
import { IMPORTO_MUTUO } from '../config/paths';
import ValoreCasa from './cerca-mutuo/valore_casa/index.js';
import ImportoMutuo from './cerca-mutuo/importo_mutuo/index.js';

const MAX_WIDTH = '1300px';

const tagComponentMap = {
  'cta-banner': CtaBanner,
  'carousel-preventivi': CarouselQuotation,
  'broker-list': BrokerList,
  'provinces-list': ProvincesList,
  'textual-quotation': TextualQuotation,
  'results-quotation': ResultsQuotation,
  // Aggiungi altri tag e componenti React secondo necessità
};
const replaceTagsWithComponents = async (content, tagComponentMap) => {
  let replacedContent = content;

  // Replace <a> tags
  replacedContent = replacedContent.replace(/<a[^>]*href="([^"]*)"[^>]*>/g, (match, hrefAttr) => {
    if (hrefAttr.includes(config.cmsBaseUrl)) {
      let replacedHref = hrefAttr.replace(config.cmsBaseUrl, '');
      if (replacedHref.endsWith('/')) {
        replacedHref = replacedHref.slice(0, -1);
      } 
      return `<a href="${replacedHref}">`;
    }
    return match;
  });

  // Define regex for shortcode tag matching
  const tagRegex = /\[([^[\]\s]+)([^[\]]*)\]/g;

  // Match all tags in content
  const matches = replacedContent.match(tagRegex);

  if (matches) {
    // Process each matched tag asynchronously
    const replacedContentArray = await Promise.all(
      matches.map(async match => {
        const result = await replaceTagWithComponent(match, tagComponentMap);
        return result;
      }),
    );

    // Replace matched tags with corresponding components
    replacedContentArray.forEach((result, index) => {
      replacedContent = replacedContent.replace(matches[index], result);
    });
  }

  return replacedContent;
};

// Function to replace a single tag with its corresponding component
const replaceTagWithComponent = async (match, tagComponentMap) => {
  const { tag, attributes } = parseTag(match);

  const TagComponent = tagComponentMap[tag];

  if (TagComponent) {
    const props = parseAttributes(attributes);
    let renderedComponent;

    switch (tag) {
      case 'carousel-preventivi':
        renderedComponent = await renderCarouselQuotationServerSide(props);
        break;
      case 'provinces-list':
        renderedComponent = await renderProvincesListServerSide(props);
        break;
      case 'broker-list':
        renderedComponent = await renderBrokerListServerSide(props);
        break;
      case 'textual-quotation':
        renderedComponent = await renderTextualQuotationServerSide(props);
        break;
      case 'results-quotation':
        renderedComponent = await renderResultsQuotationServerSide(props);
        break;
      default:
        renderedComponent = await renderGenericTag(TagComponent, props);
    }

    return renderedComponent;
  } else {
    // Return original tag if no matching component found
    return match;
  }
};

// Function to parse tag and attributes from match
const parseTag = match => {
  const [, tag, attributes] = match.match(/\[([^[\]\s]+)([^[\]]*)\]/);
  return { tag, attributes };
};

// Function to parse attributes string into props object
const parseAttributes = attributes => {
  const props = {};
  const regex = /(\w+)={([^}]*)}/g;

  let match;
  while ((match = regex.exec(attributes)) !== null) {
    props[match[1]] = match[2];
  }

  return props;
};

// Function to render generic tag component
const renderGenericTag = async (Comp, props) => {
  return ReactDOMServer.renderToString(<Comp {...props} />);
};

const replaceTagsWithComponentsClient = domNode => {
  if (domNode.type === 'tag') {
    const props = attributesToProps(domNode.attribs);

    switch (domNode.name) {
      case 'carouselquotation':
        // props are passed in children ti prevent parsing error from html-react-parser
        const carouselQuotationProps = domNode.children && domNode.children[0]?.data ? JSON.parse(domNode.children[0].data) : {};
        return <CarouselQuotation {...carouselQuotationProps} />;
      case 'provinceslist':
        return <ProvincesList {...props} />;
      case 'resultsquotation':
        // props are passed in children ti prevent parsing error from html-react-parser
        const resultsQuotationProps = domNode.children && domNode.children[0]?.data ? JSON.parse(domNode.children[0].data) : {};
        return <ResultsQuotation {...resultsQuotationProps} />;
    }
  }
  if (domNode.type === 'text' && domNode.data.startsWith('[') && domNode.data.endsWith(']')) {
    const data = domNode.data;

    const regex = /\[([^[\]\s]+)([^[\]]*)\]/g;

    let tagToRender;

    data.replace(regex, (match, tag, attributes) => {
      const TagComponent = tagComponentMap[tag];

      if (TagComponent) {
        // Definisci l'espressione regolare per estrarre le coppie chiave-valore
        const regex = /(\w+)={([^}]*)}/g;

        let match;
        let props = {};

        // Itera attraverso tutte le corrispondenze della regex nella stringa
        while ((match = regex.exec(attributes)) !== null) {
          // match[1] è la chiave e match[2] è il valore
          props[match[1]] = match[2];
        }
        tagToRender = <TagComponent {...props} />;
      }
    });

    if (tagToRender) {
      return tagToRender;
    }
  }
};

export const CmsPageContext = createContext();

const Page = ({ menuItems, contentRendered, titleRendered, head, showStepQuestion }) => {
  const { title, description, ogTitle, ogDescription, ogImage } = head;
  const router = useRouter();
  const [, setOnRendered] = useOnRenderedContext();
  const [isLoading, setIsLoading] = useState(false);

  const { valoreCasa, setValoreCasa } = useStore(
    useShallow(state => ({
      valoreCasa: state.valoreCasa,
      setValoreCasa: state.setValoreCasa,
    })),
  );
  const STEP_CONFIG = {
    totalSteps: 10,
    currentStep: 1,
    currentPercentage: 10,
    showProgressSpinner: false,
    showStepper: true,
    intro: null,
    title: null,
    description: null,
  };
  const CURRENT_QUESTION = {
    step: 0,
    question_label: "🏠 Quanto costa l'immobile che vuoi acquistare?",
    question_type: 'price',
    min_value: '30000',
    max_value: '10000000',
    var_name: 'step_1',
    required: 'true',
    show_percentage: true,
    percentage: STEP_CONFIG.currentPercentage,
    question_placeholder: 'es. 175.000',
    last_question: false,
    suggestion: 'Se non conosci il prezzo preciso inserisci un importo indicativo.',
    question_answers: [],
  };
  const next = useCallback(async () => {
      if (isLoading) return;
      try {
        setIsLoading(true);
        const params = new URLSearchParams(router.query);
        const variation = params.get('variation');
        if(variation=='1'){
          router.push(ETA + '?' + params.toString());
        } else {
          router.push(IMPORTO_MUTUO + '?' + params.toString());
        }
      } catch (error) {
        console.error('Navigation error:', error);
      } finally {
        setIsLoading(false);
      }
    }, [router, isLoading]);

  //for prevent hydration error see: https://nextjs.org/docs/messages/react-hydration-error
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);

    window.dataLayer.push({
      event: 'from_seo',
    });
  }, []);

  const goToCercaMutuoPage = async () => {
    await router.push('/cerca-mutuo?start-step-one=true#top');
  };

  const p = router.asPath.slice(1);
  const canonicalURL = `https://www.tassomutuo.it/${p}`.split('?')[0];

  const product = {
    name: ogTitle,
    description: ogDescription,
    image: ogImage,
  };
  const schemaProduct = getProductSchema(product);
  const schemaBreadcrumb = getBreadcrumbsSchema([{ menuItems, url: canonicalURL, name: ogTitle, description: ogDescription }]);

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalURL} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:image" content={ogImage} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaProduct) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaBreadcrumb) }} />
        {/*   <meta name="keywords" content={t('home.head_keywords')} />*/}
      </Head>
      <HeaderWithMenu menuItems={menuItems} />
      {showStepQuestion && (
        <div>
          <div id="classic" style={{ display: 'block' }}>
            <ValoreCasa intro={titleRendered} />
          </div>
        </div>
      )}
      <CmsPageContext.Provider value={{ menuItems, pageTitle: titleRendered, goToCercaMutuoPage }}>
        <Box
          id="cms-page"
          sx={{
            padding: '80px 20px 20px 20px',
          }}>
          <Box sx={{ margin: '0 auto', maxWidth: MAX_WIDTH }}>
            {contentRendered && (
              <>
                {(() => {
                  const contentParsed = parse(contentRendered, { replace: replaceTagsWithComponentsClient, trim: true });
                  setOnRendered(true, 'path');
                  return contentParsed;
                })()}
                {isClient && <Breadcrumbs />}
              </>
            )}
          </Box>
        </Box>
        <Box sx={{ margin: '0 auto', maxWidth: '100%' }}>
          <TrustpilotReviews />
        </Box>
      </CmsPageContext.Provider>
    </>
  );
};

export async function getServerSideProps({ query }) {
  try {
    const pathToResolve = query.path.join('/');
    const menuItemsRes = await getMenuItems();
    const { data: menuItems } = menuItemsRes;
    const res = await getPage(pathToResolve);
    const { data: dataPage } = res;

    const contentRendered = await replaceTagsWithComponents(dataPage.data.content.rendered, tagComponentMap);

    return {
      props: {
        menuItems,
        showStepQuestion: dataPage?.data?.template !== 'wp-custom-template-risultati',
        contentRendered,
        //excerptRendered: dataPage.data.excerpt.rendered,
        titleRendered: dataPage.data.title.rendered,
        head: {
          title: dataPage?.data?.yoast_head_json?.title || i18next.t('home.head_title'),
          description: dataPage?.data?.yoast_head_json?.description || i18next.t('home.head_description'),
          ogTitle: dataPage?.data?.yoast_head_json?.og_title || i18next.t('home.head_description'),
          ogDescription: dataPage?.data?.yoast_head_json?.og_description || i18next.t('home.head_description'),
          ogImage:
            dataPage?.data?.yoast_head_json?.og_image &&
            dataPage?.data?.yoast_head_json?.og_image[0] &&
            dataPage?.data?.yoast_head_json?.og_image[0]?.url
              ? dataPage?.data?.yoast_head_json?.og_image[0]?.url
              : 'https://www.tassomutuo.it/product_image.png',
        },
      },
    };
  } catch (e) {
    Sentry.captureException('Error 404 cms', {level:'fatal', extra: {e} });
    return {
      notFound: true,
    };
  }
}

export default Page;
